import { isValidPin, parseInboxKey } from '../../shared/signaling.js';

export type InboxCredentials = Readonly<{ number: string; key: string }>;

export const INBOX_STORAGE_KEY = 'syncport.inbox';

/** Key used before the rename; read once and migrated so nobody is signed out by the rebrand. */
export const LEGACY_INBOX_STORAGE_KEY = 'mystery-box.inbox';

/** Accepts only a well-formed credentials object; anything else counts as "not signed in". */
export const parseStoredCredentials = (raw: string | null): InboxCredentials | null => {
  if (raw === null) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) {
    return null;
  }
  const { number, key } = parsed as Record<string, unknown>;
  if (typeof number !== 'string' || !isValidPin(number) || typeof key !== 'string') {
    return null;
  }
  try {
    parseInboxKey(key);
  } catch {
    return null;
  }
  return { number, key };
};

/** localStorage has no expiry, which is exactly what an always-on inbox wants: it stays until forgotten. */
export const loadInboxCredentials = (): InboxCredentials | null => {
  const current = parseStoredCredentials(window.localStorage.getItem(INBOX_STORAGE_KEY));
  if (current !== null) {
    return current;
  }
  const legacy = parseStoredCredentials(window.localStorage.getItem(LEGACY_INBOX_STORAGE_KEY));
  if (legacy !== null) {
    window.localStorage.setItem(INBOX_STORAGE_KEY, JSON.stringify(legacy));
    window.localStorage.removeItem(LEGACY_INBOX_STORAGE_KEY);
  }
  return legacy;
};

export const saveInboxCredentials = (credentials: InboxCredentials): void =>
  window.localStorage.setItem(INBOX_STORAGE_KEY, JSON.stringify(credentials));

export const forgetInboxCredentials = (): void => {
  window.localStorage.removeItem(INBOX_STORAGE_KEY);
  window.localStorage.removeItem(LEGACY_INBOX_STORAGE_KEY);
};
